(() => {
  'use strict';
  const configElement = document.getElementById('baf-search-config');
  if (!configElement || window.BafticSearchV2) return;
  const config = JSON.parse(configElement.textContent);
  const labels = config.labels;
  const normalize = value => String(value ?? '').normalize('NFKD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/[’']/g, '').replace(/[^a-z0-9]+/g, ' ').trim();
  const clean = value => String(value ?? '').replace(/title:/gi, '').replace(/\*/g, '').trim();
  const flatten = value => Array.isArray(value) ? value.map(flatten).join(' ') : (value && typeof value === 'object' ? Object.values(value).map(flatten).join(' ') : String(value ?? ''));
  const escapeRE = value => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const aliases = {
    dial_color: ['dial color', 'dial colour', 'dial'],
    strap_color: ['strap color', 'strap colour', 'band color', 'band colour', 'strap', 'band'],
    strap_material: ['strap material', 'band material', 'strap', 'band'],
    dial_material: ['dial material'],
    case_material: ['case material', 'case'],
    case_shape: ['case shape', 'shape', 'case'],
    movement: ['movement'],
    gender: ['gender'],
    dial_size: ['dial size', 'dial diameter', 'dial'],
    water_resistance: ['water resistance', 'water resistant'],
    glass: ['glass', 'crystal'],
    case_thickness: ['case thickness', 'thickness'],
    clasp: ['clasp'],
    country_origin: ['country of origin', 'origin']
  };
  let catalogPromise;
  let rules = [];
  const money = new Intl.NumberFormat(config.locale || 'en-IN', { style: 'currency', currency: config.currency });
  function prepare(products) {
    const values = new Map();
    const prepared = products.map(product => {
      const attrs = Object.fromEntries(Object.entries(product.attrs || {}).map(([key, value]) => [key, normalize(flatten(value))]));
      for (const [key, value] of Object.entries(attrs)) {
        if (!aliases[key] || !value || value.length > 70) continue;
        if (!values.has(key)) values.set(key, new Set());
        values.get(key).add(value);
      }
      const title = normalize(product.title);
      const skuText = (product.skus || []).map(normalize);
      return { ...product, attrs, _title: title, _skus: skuText, _text: normalize([title, ...skuText, product.vendor, product.type, ...Object.values(attrs)].join(' ')) };
    });
    rules = [];
    for (const [key, options] of values) for (const value of options) for (const alias of aliases[key]) {
      rules.push({ key, value, text: `${value} ${alias}` });
      rules.push({ key, value, text: `${alias} ${value}` });
    }
    rules.sort((a, b) => b.text.length - a.text.length);
    return prepared;
  }
  async function load() {
    if (catalogPromise) return catalogPromise;
    catalogPromise = (async () => {
      const base = new URL(config.indexUrl, location.origin);
      const params = new URLSearchParams(location.search);
      if (params.has('preview_theme_id')) base.searchParams.set('preview_theme_id', params.get('preview_theme_id'));
      async function page(number) {
        const url = new URL(base);
        url.searchParams.set('page', number);
        const response = await fetch(url, { credentials: 'same-origin', signal: AbortSignal.timeout(30000) });
        if (!response.ok) throw new Error('Catalog request failed (' + response.status + ')');
        const data = await response.json();
        if (!Array.isArray(data.products) || data.page !== number) throw new Error('Invalid catalog response for page ' + number);
        return data;
      }
      const cacheKey = 'baftic-search-v2.1:' + config.currency + ':' + config.locale;
      try {
        const cached = JSON.parse(sessionStorage.getItem(cacheKey) || 'null');
        if (cached && Date.now() - cached.time < 120000 && Array.isArray(cached.products)) return prepare(cached.products);
      } catch (_) {}
      const first = await page(1);
      if (first.total > 25000) throw new Error('Catalog requires a server search index');
      const products = [...first.products];
      for (let start = 2; start <= first.pages; start += 3) {
        const batch = await Promise.all(Array.from({ length: Math.min(3, first.pages - start + 1) }, (_, i) => page(start + i)));
        for (const data of batch) products.push(...data.products);
      }
      if (products.length !== first.total) throw new Error('Incomplete catalog response: ' + products.length + '/' + first.total);
      const unique = [...new Map(products.map(product => [product.id, product])).values()];
      try { sessionStorage.setItem(cacheKey, JSON.stringify({ time: Date.now(), products: unique })); } catch (_) {}
      return prepare(unique);
    })().catch(error => { console.warn('[Baftic search]', error.message); catalogPromise = undefined; throw error; });
    return catalogPromise;
  }
  function parse(query) {
    let remaining = ` ${normalize(clean(query))} `;
    const constraints = [];
    for (const rule of rules) {
      const expression = new RegExp(`(^| )${escapeRE(rule.text)}(?= |$)`);
      if (!expression.test(remaining)) continue;
      remaining = remaining.replace(expression, ' ');
      constraints.push(rule);
    }
    const synonyms = { watches: 'watch', mens: 'men', womens: 'women', gents: 'men', ladies: 'women', colour: 'color' };
    const terms = remaining.trim().split(/\s+/).filter(Boolean).filter(term => !['with', 'and', 'for', 'the', 'a', 'an'].includes(term)).map(term => synonyms[term] || term);
    return { constraints, terms };
  }
  function matching(products, query, availableOnly = true) {
    const { constraints, terms } = parse(query);
    if (!normalize(clean(query))) return [];
    return products.filter(product => {
      if (availableOnly && !product.available) return false;
      if (!constraints.every(rule => product.attrs[rule.key] === rule.value)) return false;
      return terms.every(term => {
        if (term === 'men' || term === 'women') return product.attrs.gender ? product.attrs.gender.split(' ').includes(term) || product.attrs.gender.includes('unisex') : product._text.split(' ').includes(term);
        return product._text.includes(term) || product._skus.some(sku => sku.replace(/\s/g, '').includes(term.replace(/\s/g, '')));
      });
    }).sort((a, b) => {
      const term = normalize(clean(query));
      const score = product => product._skus.includes(term) ? 4 : product._title === term ? 3 : product._title.includes(term) ? 2 : 1;
      return score(b) - score(a) || a.title.localeCompare(b.title);
    });
  }
  const engine = { load, matching, normalize, clean };
  window.BafticSearchV2 = engine;
  const make = (tag, className, text) => {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (text !== undefined) node.textContent = text;
    return node;
  };
  const productUrl = product => {
    const url = new URL(product.url, location.origin);
    const preview = new URLSearchParams(location.search).get('preview_theme_id');
    if (preview) url.searchParams.set('preview_theme_id', preview);
    return url.pathname + url.search;
  };
  function card(product, compact) {
    const link = make('a', compact ? 'baf-search__product' : 'baf-result');
    link.href = productUrl(product);
    if (product.image) {
      const image = make('img', compact ? 'baf-search__image' : 'baf-result__image');
      image.src = product.image;
      image.alt = product.title;
      image.loading = 'lazy';
      image.width = compact ? 84 : 480;
      image.height = compact ? 84 : 480;
      link.appendChild(image);
    }
    const copy = make('span', compact ? 'baf-search__product-copy' : 'baf-result__copy');
    copy.appendChild(make('span', compact ? 'baf-search__vendor' : 'baf-result__vendor', product.vendor));
    copy.appendChild(make('span', compact ? 'baf-search__product-title' : 'baf-result__title', product.title));
    copy.appendChild(make('span', compact ? 'baf-search__price' : 'baf-result__price', money.format(product.price / 100)));
    if (!product.available) copy.appendChild(make('span', 'baf-result__soldout', labels.soldOut));
    link.appendChild(copy);
    return link;
  }
  const searchUrl = query => {
    const url = new URL(config.searchUrl, location.origin);
    url.searchParams.set('q', clean(query));
    url.searchParams.set('type', 'product');
    url.searchParams.set('options[unavailable_products]', 'hide');
    const preview = new URLSearchParams(location.search).get('preview_theme_id');
    if (preview) url.searchParams.set('preview_theme_id', preview);
    return url.pathname + url.search;
  };
  const dialog = document.getElementById('BafSearchDialog');
  if (dialog) {
    const input = dialog.querySelector('[data-baf-search-input]');
    const results = dialog.querySelector('[data-baf-search-results]');
    const status = dialog.querySelector('[data-baf-search-status]');
    let timer, requestId = 0;
    input.addEventListener('input', () => {
      clearTimeout(timer);
      const query = input.value.trim(), id = ++requestId;
      results.replaceChildren();
      results.hidden = query.length < 2;
      input.setAttribute('aria-expanded', String(query.length >= 2));
      if (query.length < 2) { status.textContent = query.length ? labels.moreCharacters : ''; return; }
      status.textContent = labels.searching;
      results.appendChild(make('p', 'baf-search__empty', labels.searching));
      timer = setTimeout(async () => {
        try {
          const matches = matching(await load(), query);
          if (id !== requestId) return;
          results.replaceChildren();
          if (!matches.length) results.appendChild(make('p', 'baf-search__empty', labels.noResults));
          const grid = make('div', 'baf-search__products');
          matches.slice(0, 6).forEach(product => grid.appendChild(card(product, true)));
          results.appendChild(grid);
          const all = make('a', 'baf-search__all', `${labels.viewAll} (${matches.length})`);
          all.href = searchUrl(query);
          results.appendChild(all);
          status.textContent = `${matches.length} ${labels.results} “${query}”`;
        } catch (_) {
          if (id !== requestId) return;
          status.textContent = labels.error;
          results.replaceChildren(make('p', 'baf-search__empty', labels.error));
        }
      }, 220);
    });
    dialog.querySelector('form').addEventListener('submit', event => {
      event.preventDefault();
      if (clean(input.value)) location.assign(searchUrl(input.value));
    });
    document.addEventListener('click', event => {
      if (event.target.closest('[data-baf-search-open]')) {
        event.preventDefault();
        if (!dialog.open) dialog.showModal();
        input.focus();
      }
      if (event.target.closest('[data-baf-search-close]')) dialog.close();
    });
    dialog.addEventListener('click', event => { if (event.target === dialog) dialog.close(); });
  }
  const page = document.querySelector('[data-baf-results-page]');
  if (page) {
    const params = new URLSearchParams(location.search);
    const query = clean(params.get('q') || '');
    const input = page.querySelector('input[name="q"]');
    const form = page.querySelector('form');
    const status = page.querySelector('[data-baf-results-status]');
    const grid = page.querySelector('[data-baf-results-grid]');
    const pagination = page.querySelector('[data-baf-results-pagination]');
    const sort = page.querySelector('[data-baf-results-sort]');
    const soldout = page.querySelector('[data-baf-results-soldout]');
    input.value = query;
    form.addEventListener('submit', event => { event.preventDefault(); if (clean(input.value)) location.assign(searchUrl(input.value)); });
    let products, currentPage = Math.max(1, Number(params.get('baf_page')) || 1);
    const pageSize = 24;
    function render() {
      const found = matching(products, query, !soldout.checked);
      if (sort.value === 'price-ascending') found.sort((a, b) => a.price - b.price);
      if (sort.value === 'price-descending') found.sort((a, b) => b.price - a.price);
      if (sort.value === 'title-ascending') found.sort((a, b) => a.title.localeCompare(b.title));
      const pages = Math.ceil(found.length / pageSize);
      currentPage = Math.min(currentPage, Math.max(1, pages));
      status.textContent = found.length ? `${found.length} ${labels.results} “${query}”` : `${labels.noResults} “${query}”`;
      grid.replaceChildren(...found.slice((currentPage - 1) * pageSize, currentPage * pageSize).map(product => card(product, false)));
      pagination.replaceChildren();
      for (let number = 1; number <= pages; number++) {
        if (pages > 9 && number !== 1 && number !== pages && Math.abs(number - currentPage) > 2) continue;
        const button = make('button', 'baf-results__page', String(number));
        button.type = 'button';
        button.setAttribute('aria-label', `${labels.page} ${number}`);
        if (number === currentPage) button.setAttribute('aria-current', 'page');
        button.addEventListener('click', () => {
          currentPage = number;
          const url = new URL(location.href);
          url.searchParams.set('baf_page', number);
          history.replaceState(null, '', url);
          render();
          page.scrollIntoView({ behavior: 'smooth', block: 'start' });
        });
        pagination.appendChild(button);
      }
      sort.disabled = false;
      soldout.disabled = false;
      page.setAttribute('aria-busy', 'false');
    }
    for (const control of [sort, soldout]) control.addEventListener('change', () => { currentPage = 1; render(); });
    if (query) {
      status.textContent = labels.searching;
      page.setAttribute('aria-busy', 'true');
      load().then(data => { products = data; render(); }).catch(() => {
        page.setAttribute('aria-busy', 'false');
        status.textContent = labels.error;
        const retry = make('button', 'baf-results__page', labels.retry);
        retry.type = 'button';
        retry.addEventListener('click', () => location.reload());
        pagination.replaceChildren(retry);
      });
    } else status.textContent = labels.enterSearch;
  }
})();
